import {
  ColumnFiltersState,
  createColumnHelper,
  getFilteredRowModel,
  PaginationState,
  SortingState
} from '@tanstack/table-core';
import clsx from 'clsx';
import React, {
  FormEvent,
  Suspense,
  useCallback,
  useMemo,
  useState
} from 'react';
import {
  FiCode,
  FiCodepen,
  FiEdit2,
  FiEye,
  FiInfo,
  FiMessageSquare,
  FiPlus
} from 'react-icons/fi';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import APIGateway from '@/api';
import { AgentActionMenu } from '@/app/agents/agentActionMenu';
import { Button } from '@/components/button/button';
import { DeploymentStatusBadge } from '@/components/deploymentStatus/deploymentStatus';
import { Menu, MenuItem } from '@/components/dropdownMenu/dropdownMenu';
import { Icon } from '@/components/icon/icon';
import { Input } from '@/components/input/input';
import { Loader } from '@/components/loader/loader';
import Modal from '@/components/modal/modal';
import { NativeTable } from '@/components/nativeTable/nativeTable';
import { useNativeTable } from '@/components/nativeTable/useNativeTable';
import {
  buildColumnClassString,
  ColumnModEnums
} from '@/components/table/table';
import { Tooltip } from '@/components/tooltip/tooltip';
import { Heading } from '@/components/typography/heading';
import { Text } from '@/components/typography/text';
import { useAgentsQuery } from '@/hooks/queries/useAgents';
import { useByoaAgentsQuery } from '@/hooks/queries/useByoaAgents';
import {
  AgentAidp,
  AgentConfigStepLabelEnum,
  AgentConfigStepRouteOutputTypeEnum,
  AgentConfigStepTypeEnum,
  AgentCreateApiRes,
  AgentCreateArgs,
  AgentLowcode,
  AgentTypeEnum,
  BaseAgent,
  ByoaAgent,
  DeploymentStatus
} from '@/types/agent';
import {
  getLatestVersionEntry,
  getLowCodeAgentDisplayId
} from '@/utils/agentUtils';
import { envVarsProvider } from '@/utils/envVars/envVars';

import ModalLowCodeAgentCreate, { ModalLowCodeAgentCreateRet } from './_modalLowCodeAgentCreate/modalLowCodeAgentCreate';
import pageStyles from './page.module.scss';


type TypeFilter = AgentTypeEnum | 'all';

const TYPE_FILTER_LABELS: Record<TypeFilter, string> = {
  all: 'All Agents',
  [ AgentTypeEnum.LOW_CODE ]: 'Low-Code Agents',
  [ AgentTypeEnum.AIDP ]: 'Full-Code Agents'
};

const DOCS_BASE_URL = envVarsProvider.get( 'DOCS_BASE_URL' );
const FULL_CODE_DOCS_URL = DOCS_BASE_URL + '/getting_started';

const columnHelper = createColumnHelper<BaseAgent>();

const getConstellationName = ( agent: BaseAgent ): string => {
  if ( agent.type !== AgentTypeEnum.AIDP ) return '-';

  const aidpData = agent.data as AgentAidp;
  return aidpData.constellationName ?? agent.constellationName ?? '-';
};

const getAgentId = ( agent: BaseAgent ): string => {
  if ( agent.type !== AgentTypeEnum.AIDP ) {
    return getLowCodeAgentDisplayId( agent.name ?? '' );
  };

  const aidpData = agent.data as AgentAidp;
  return aidpData.agentId ?? '';
};


function AgentsPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [ columnFilters, setColumnFilters ] = useState<ColumnFiltersState>( [] );
  const [ pagination, setPagination ] = useState<PaginationState>( {
    pageIndex: 0,
    pageSize: 30
  } );
  const [ showCreate, setShowCreate ] = useState<boolean>( false );
  const [ showCreateFullCode, setShowCreateFullCode ] = useState<boolean>( false );
  const [ sorting, setSorting ] = useState<SortingState>( [
    { id: 'name', desc: false }
  ] );
  const [ typeFilter, setTypeFilter ] = useState<TypeFilter>( 'all' );
  const [ constellationFilter, setConstellationFilter ] = useState<string>( () => ( location.state as { constellationFilter?: string } )?.constellationFilter ?? '' );

  const { isPending, error, data: agents } = useAgentsQuery( { enabled: true } );
  const { data: byoaAgents } = useByoaAgentsQuery();

  const byoaAgentsByName = useMemo( () => {
    const map = new Map<string, ByoaAgent>();
    byoaAgents?.forEach( ( agent ) => map.set( agent.name, agent ) );
    return map;
  }, [ byoaAgents ] );

  if ( error ) {
    console.error( `unable to get agents: ${ error }` );
    toast.error( 'unable to get agents' );
  }

  const filteredAgents = useMemo( () => {
    let result = agents ?? [];

    if ( typeFilter !== 'all' ) {
      result = result.filter( ( agent ) => agent.type === typeFilter );
    }

    if ( constellationFilter.trim() ) {
      const query = constellationFilter.trim().toLowerCase();
      result = result.filter( ( agent ) => {
        const constellation = getConstellationName( agent );
        return (
          constellation !== '-' && constellation.toLowerCase().includes( query )
        );
      } );
    }

    return result;
  }, [ agents, typeFilter, constellationFilter ] );

  const onCreateLowCodeAgent = ( lowCodeCreateArgs: ModalLowCodeAgentCreateRet ) => {
    const {
      description,
      name
    }: ModalLowCodeAgentCreateRet = lowCodeCreateArgs;

    const args: AgentCreateArgs = {
      name,
      description,
      steps: [
        {
          id: AgentConfigStepLabelEnum.START,
          label: AgentConfigStepLabelEnum.START,
          description: AgentConfigStepLabelEnum.START,
          inputs: {
            conversation: { type: 'list' }
          },
          type: AgentConfigStepTypeEnum.START,
          routes: {
            default: {
              outputs: {
                conversation: {
                  type: AgentConfigStepRouteOutputTypeEnum.FROM_INPUT,
                  key: 'conversation'
                }
              },
              step: AgentConfigStepLabelEnum.END
            }
          }
        },
        {
          id: AgentConfigStepLabelEnum.END,
          label: AgentConfigStepLabelEnum.END,
          description: AgentConfigStepLabelEnum.END,
          inputs: {},
          type: AgentConfigStepTypeEnum.END,
          routes: {
            default: {
              outputs: {},
              step: AgentConfigStepLabelEnum.END
            }
          }
        }
      ]
    };

    APIGateway.agents
      .agentCreate( args )
      .then( ( data: AgentCreateApiRes ) => {
        navigate(
          `/agents/edit/?${ new URLSearchParams( { id: data.agentId, version: String( data.version ) } ) }`
        );
      } )
      .catch( ( e: Error ) => {
        console.error( 'unable to create agent', e );
        toast( 'unable to create agent' );
      } )
      .finally( () => {
        onCreateHide();
      } );
  };

  const onCreateHide = () => setShowCreate( false );
  const onCreateShow = () => setShowCreate( true );

  const handleRowClick = useCallback(
    ( agent: BaseAgent ) => {
      if ( agent.type === AgentTypeEnum.LOW_CODE.toString() ) {
        const lowcodeAgent = agent as BaseAgent<AgentLowcode>;
        const latestVersion = getLatestVersionEntry(
          lowcodeAgent.data.versions
        ).version;
        navigate(
          `/agents/details/?${ new URLSearchParams( { id: lowcodeAgent.data.agentId, version: String( latestVersion ) } ) }`
        );
      } else if ( agent.type === AgentTypeEnum.AIDP.toString() ) {
        navigate(
          `/agents/details/?${ new URLSearchParams( { id: agent.id, type: 'aidp' } ) }`
        );
      }
    },
    [ navigate ]
  );

  const columns = useMemo(
    () => [
      columnHelper.display( {
        id: 'type',
        header: 'Agent Type',
        size: 140,
        cell: ( info ) => {
          const el = info.row.original;
          if ( el.type === AgentTypeEnum.LOW_CODE.toString() ) {
            return (
              <span className={ pageStyles.agent_type_cell }>
                <Tooltip content="Low-Code Agent" position="top">
                  <Icon icon={ FiCode } size="md" />
                </Tooltip>
                Low-Code
              </span>
            );
          } else if ( el.type === AgentTypeEnum.AIDP.toString() ) {
            return (
              <span className={ pageStyles.agent_type_cell }>
                <Tooltip content="Full-Code Agent" position="top">
                  <Icon icon={ FiCodepen } size="md" />
                </Tooltip>
                Full-Code
              </span>
            );
          }
          return <></>;
        },
        meta: {
          classStr: buildColumnClassString( { width: 12 } )
        }
      } ),
      columnHelper.accessor( ( agent ) => getConstellationName( agent ), {
        id: 'constellation',
        header: 'Constellation',
        size: 160,
        enableSorting: true,
        sortingFn: 'alphanumeric',
        sortDescFirst: false,
        meta: {
          classStr: buildColumnClassString( { width: 13 } )
        }
      } ),
      columnHelper.accessor( 'name', {
        header: 'Agent Name (version)',
        size: 300,
        cell: ( info ) => {
          const el = info.row.original;
          let version = '';
          if ( el.type === AgentTypeEnum.LOW_CODE.toString() ) {
            const agent = el as BaseAgent<AgentLowcode>;
            version = getLatestVersionEntry(
              agent.data.versions
            ).version.toString();
          }

          const name = info.getValue();
          const displayName =
            el.type === AgentTypeEnum.AIDP.toString()
              ? ( ( el.data as AgentAidp ).agentName ?? name )
              : name;
          return version ? displayName + ` (${ version })` : displayName;
        },
        enableSorting: true,
        sortingFn: 'alphanumeric',
        sortDescFirst: false,
        meta: {
          classStr: buildColumnClassString( { width: 25 } )
        }
      } ),
      columnHelper.display( {
        id: 'deploymentStatus',
        header: 'Deployment Status',
        size: 160,
        cell: ( info ) => {
          const el = info.row.original;
          if ( el.type === AgentTypeEnum.LOW_CODE.toString() ) {
            return <DeploymentStatusBadge status={ DeploymentStatus.RUNNING } />;
          }
          if ( el.type !== AgentTypeEnum.AIDP.toString() ) return null;
          const aidpData = el.data as AgentAidp;
          // aidp-core keys by constellation name; fall back to agent name for old-SDK.
          const byoaAgent = byoaAgentsByName.get(
            aidpData.constellationName ?? el.constellationName ?? aidpData.agentName ?? el.name
          );
          if ( !byoaAgent ) return '-';
          return (
            <DeploymentStatusBadge
              status={ byoaAgent.status as DeploymentStatus }
            />
          );
        },
        meta: {
          classStr: buildColumnClassString( { width: 15 } )
        }
      } ),
      columnHelper.display( {
        id: 'agentId',
        header: 'Agent ID',
        size: 300,
        cell: ( info ) => {
          const el = info.row.original;
          return getAgentId( el );
        },
        meta: {
          classStr: buildColumnClassString( { width: 25 } )
        }
      } ),
      columnHelper.display( {
        id: 'play',
        header: 'Play',
        size: 80,
        cell: ( info ) => {
          const el = info.row.original;
          const name = el.name;
          const type = el.type;

          if ( el.type === AgentTypeEnum.LOW_CODE.toString() ) {
            const agent = el as BaseAgent<AgentLowcode>;
            const latestVersionAgent = getLatestVersionEntry(
              agent.data.versions
            );

            return (
              <Link
                className={ pageStyles.action_icon }
                to={ `/agents/playground/?${ new URLSearchParams( { an: name, i: agent.data.agentId, t: type, v: String( latestVersionAgent.version ) } ) }` }
              >
                <Icon icon={ FiMessageSquare } size="md" />
              </Link>
            );
          } else if ( el.type === AgentTypeEnum.AIDP.toString() ) {
            return (
              <Link
                className={ pageStyles.action_icon }
                to={ `/agents/playground/?${ new URLSearchParams( { an: el.id, t: type } ) }` }
              >
                <Icon icon={ FiMessageSquare } size="md" />
              </Link>
            );
          }
          return <></>;
        },
        meta: {
          classStr: buildColumnClassString( {
            width: 5,
            styles: ColumnModEnums.CENTER
          } ),
          centerHeader: true
        }
      } ),
      columnHelper.display( {
        id: 'config',
        header: 'Low-Code Flow',
        size: 80,
        cell: ( info ) => {
          const el = info.row.original;

          if ( el.type === AgentTypeEnum.LOW_CODE.toString() ) {
            const agent = el as BaseAgent<AgentLowcode>;
            const latestVersionAgent = getLatestVersionEntry(
              agent.data.versions
            );

            return (
              <Link
                className={ pageStyles.action_icon }
                to={ `/agents/edit/?${ new URLSearchParams( { id: agent.data.agentId, version: String( latestVersionAgent.version ) } ) }` }
              >
                <Icon icon={ el.editable ? FiEdit2 : FiEye } size="md" />
              </Link>
            );
          } else if ( el.type === AgentTypeEnum.AIDP.toString() ) {
            return '-';
          }
          return <></>;
        },
        meta: {
          classStr: buildColumnClassString( {
            width: 5,
            styles: ColumnModEnums.CENTER
          } ),
          centerHeader: true
        }
      } ),
      columnHelper.display( {
        id: 'actions',
        header: 'Actions',
        size: 80,
        cell: ( info ) => {
          const el = info.row.original;

          if ( el.type === AgentTypeEnum.LOW_CODE.toString() ) {
            const lowcodeData = el.data as AgentLowcode;
            return el.editable ? (
              <AgentActionMenu
                agentId={ lowcodeData.agentId }
                agentName={ getLowCodeAgentDisplayId( el.name ) }
                isAidp={ false }
                isPublic={ el.isPublic }
                isOff={ lowcodeData.deploymentStatus === DeploymentStatus.OFF }
              />
            ) : null;
          } else if ( el.type === AgentTypeEnum.AIDP.toString() ) {
            const aidpData = el.data as AgentAidp;
            const shortName = aidpData.agentName ?? el.name;
            const byoaLookup = aidpData.constellationName ?? el.constellationName ?? shortName;
            const byoaAgent = byoaAgentsByName.get( byoaLookup );
            return el.editable ? (
              <AgentActionMenu
                agentId={ aidpData.agentId ?? byoaAgent?.id ?? el.name }
                agentName={ el.name }
                isAidp={ true }
                isPublic={ el.isPublic }
                isOff={ byoaAgent?.status === DeploymentStatus.OFF }
                byoaAgent={ byoaAgent }
              />
            ) : null;
          }
          return <></>;
        },
        meta: {
          classStr: buildColumnClassString( {
            width: 5,
            styles: ColumnModEnums.CENTER
          } ),
          centerHeader: true
        }
      } )
    ],
    [ byoaAgentsByName ]
  );

  const tableState = useMemo(
    () => ( {
      columnFilters,
      pagination,
      sorting
    } ),
    [ columnFilters, pagination, sorting ]
  );

  const table = useNativeTable<BaseAgent>( {
    columns,
    data: filteredAgents,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    state: tableState
  } );

  const handleFilterChange = useCallback(
    ( e: React.ChangeEvent<HTMLInputElement> ) => {
      table.getColumn( 'name' )?.setFilterValue( e.target.value );
    },
    [ table ]
  );

  const handleTypeFilterChange = useCallback( ( value: TypeFilter ) => {
    setTypeFilter( value );
    setPagination( ( prev ) => ( { ...prev, pageIndex: 0 } ) );
  }, [] );

  const handleConstellationFilterChange = useCallback(
    ( e: React.ChangeEvent<HTMLInputElement> ) => {
      setConstellationFilter( e.target.value );
      setPagination( ( prev ) => ( { ...prev, pageIndex: 0 } ) );
    },
    []
  );

  return (
    <div className="page">
      <ToastContainer />

      { isPending ? (
        <Loader />
      ) : (
        <>
          { showCreate && (
            <ModalLowCodeAgentCreate
              cb={ onCreateLowCodeAgent }
              onClose={ onCreateHide }
            />
          )}

          { showCreateFullCode && (
            <Modal className={ pageStyles.full_code_modal }>
              <Heading as="h2" size="md">
                Creating Full-Code Agents
              </Heading>
              <Text
                as="p"
                size="sm"
                className={ pageStyles.full_code_modal_body }
              >
                A <strong>Full-Code Agent</strong> is developed in Python using
                the KanpAI™ SDK and deployed via the KanpAI™ Command Line
                Interface (CLI). Full-Code Agents are part of a{' '}
                <strong>Constellation</strong>, a bundle of one or more agents.
              </Text>
              <Text
                as="p"
                size="sm"
                className={ pageStyles.full_code_modal_body }
              >
                For a step-by-step guide to creating Full-Code Agents and
                Constellations, see the{' '}
                <strong>Getting Started documentation</strong>.
              </Text>
              <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={ () => setShowCreateFullCode( false ) }
                >
                  Cancel
                </Button>
                <a
                  href={ FULL_CODE_DOCS_URL }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="primary" size="sm">
                    View Getting Started
                  </Button>
                </a>
              </div>
            </Modal>
          )}

          <div className={ clsx( 'layout__row', 'pad', 'layout__info' ) }>
            <span className="info_name">
              <Heading as="h2">Agents</Heading>
            </span>
            <div className={ clsx( 'layout__row' ) }>
              <Button
                variant="secondary"
                icon={ <Icon icon={ FiInfo } /> }
                onClick={ () => setShowCreateFullCode( true ) }
              >
                Create Full-Code Agent
              </Button>
              <Button
                variant="primary"
                icon={ <Icon icon={ FiPlus } /> }
                onClick={ onCreateShow }
              >
                Create Low-Code Agent
              </Button>
            </div>
          </div>

          <div className={ pageStyles.filter_row }>
            <Menu
              variant="select"
              label="Filter by type"
              value={ TYPE_FILTER_LABELS[typeFilter] }
              data-testid="agents-type-filter"
            >
              <MenuItem
                value="All Agents"
                onClick={ () => handleTypeFilterChange( 'all' ) }
              />
              <MenuItem
                value="Low-Code Agents"
                onClick={ () => handleTypeFilterChange( AgentTypeEnum.LOW_CODE ) }
              />
              <MenuItem
                value="Full-Code Agents"
                onClick={ () => handleTypeFilterChange( AgentTypeEnum.AIDP ) }
              />
            </Menu>
            <Input
              type="text"
              label="Filter by agent name"
              placeholder="Filter by agent name..."
              data-testid="agents-filter-input"
              value={
                ( table.getColumn( 'name' )?.getFilterValue() as string ) ?? ''
              }
              onChange={ handleFilterChange }
              className="page_filter_input"
            />
            <Input
              type="text"
              label="Filter by constellation"
              placeholder="Filter by constellation..."
              data-testid="agents-constellation-filter-input"
              value={ constellationFilter }
              onChange={ handleConstellationFilterChange }
              className="page_filter_input"
            />
          </div>

          <Text as="p" size="sm" className={ pageStyles.page_description }>
            Agents may be <strong>Low-Code Agents</strong> built in the visual
            flow editor or <strong>Full-Code Agents</strong> built and deployed
            with the KanpAI™ SDK and CLI. Use the <strong>Agent ID</strong> to
            reference or call any agent via API.
          </Text>

          <NativeTable table={ table } onRowClick={ handleRowClick } />
        </>
      ) }
    </div>
  );
}

export default function Page() {
  return (
    <Suspense fallback={ <Loader /> }>
      <AgentsPage />
    </Suspense>
  );
}
